import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, X, UserCircle2, Store, CheckCircle2, AlertTriangle } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Iter-283 — Vendor Poster section for the public post-ad form.
 *
 * Behaviour:
 *   • Only renders when GET /api/auth/me returns `can_post_for_vendors: true`.
 *     Members without the flag see zero difference.
 *   • Vendor picker searches `/api/classifieds/poster/vendors?q=`.
 *     Empty q shows the last-typed matches (top 10). 403 = rights revoked
 *     mid-session — caller notified via `onRightsRevoked`.
 *   • Selected vendor renders a pill with ✕ to clear.
 *
 * All submit-time behaviour (checklist gate + POST /poster/listings) lives
 * in PostAdPage — this component just owns the identity of the vendor.
 */

const PICKER_DEBOUNCE_MS = 220;

export default function VendorPosterSection({ vendor, onChange, onRightsRevoked }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), PICKER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const searchQ = useQuery({
    queryKey: ["poster-vendors", debounced],
    // Iter-283 fix (iteration_110): require 2+ chars before firing, so we
    // never hit the endpoint on focus or a single keystroke.
    enabled: !vendor && open && debounced.length >= 2,
    staleTime: 15_000,
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/poster/vendors", {
        params: { q: debounced },
      });
      return Array.isArray(data?.users) ? data.users.slice(0, 10) : [];
    },
  });

  // 403 → rights revoked mid-session. Bubble up so the parent can hide
  // this whole section and toast the user once.
  useEffect(() => {
    const status = searchQ.error?.response?.status;
    if (status === 403) onRightsRevoked?.();
  }, [searchQ.error, onRightsRevoked]);

  const results = searchQ.data || [];

  const pick = (v) => {
    onChange?.(v);
    setQ("");
    setOpen(false);
  };
  const clear = () => onChange?.(null);

  return (
    <div
      data-testid="vendor-poster-section"
      className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 md:p-6 mt-6"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white text-amber-600 grid place-items-center shrink-0 shadow-sm">
          <Store className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-black uppercase tracking-[0.14em] text-amber-700">
            Posting for a vendor?
          </div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Search by email, name or phone. Leave blank to post as yourself — the ad works exactly the same.
          </p>
        </div>
      </div>

      {vendor ? (
        <div className="mt-4">
          <div
            data-testid="vendor-poster-selected"
            className="inline-flex items-center gap-3 rounded-full bg-white border border-amber-300 pl-2 pr-1 py-1"
          >
            <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 grid place-items-center">
              <UserCircle2 className="w-4 h-4" />
            </span>
            <span className="text-sm">
              <span className="font-bold text-slate-900" data-testid="vendor-poster-selected-name">
                {vendor.full_name || vendor.email}
              </span>
              {vendor.full_name && (
                <span className="text-slate-500 ml-1.5">{vendor.email}</span>
              )}
            </span>
            <button
              type="button"
              onClick={clear}
              aria-label="Clear vendor"
              data-testid="vendor-poster-clear"
              className="w-7 h-7 grid place-items-center rounded-full text-slate-500 hover:bg-red-50 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-2 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> This ad will land in {vendor.email}&apos;s account.
          </p>
        </div>
      ) : (
        <div className="mt-4 relative">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="vendor-poster-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder="Search vendor by email, name or phone"
              className="w-full bg-white border border-amber-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
            {searchQ.isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>

          {open && (results.length > 0 || (debounced.length >= 2 && !searchQ.isFetching)) && (
            <div
              data-testid="vendor-poster-results"
              className="absolute left-0 right-0 top-full mt-2 z-20 rounded-xl bg-white border border-soft shadow-lg overflow-hidden"
            >
              {results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500">
                  No vendors match &quot;{debounced}&quot;.
                </div>
              ) : (
                <ul className="max-h-72 overflow-auto">
                  {results.map((v) => (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => pick(v)}
                        data-testid={`vendor-poster-result-${v.id}`}
                        className="w-full text-left px-4 py-2.5 hover:bg-amber-50 flex items-center gap-3"
                      >
                        <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                          <UserCircle2 className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {v.full_name || v.email}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {v.email}{v.phone ? ` · ${v.phone}` : ""}
                            {typeof v.active_ads === "number" ? ` · ${v.active_ads} live ads` : ""}
                          </div>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 8-point Final Check checklist                                */
/* ─────────────────────────────────────────────────────────── */

// Verbatim copy from Iter-281 §4.b — do not paraphrase.
export const PRE_SUBMIT_CHECKS = [
  "Correct vendor selected (name & email double-checked)",
  "Category is the right one for this ad",
  "Title & description read through — no typos, accurate info",
  "Price & price type verified with the vendor",
  "Location (region · city · suburb) is correct",
  "Photos reviewed — clear, right order, nothing wrong included",
  "Contact details (phone / WhatsApp) verified",
  "Vendor gave permission to post this ad on their behalf",
];

/**
 * Renders the mandatory FINAL CHECK BEFORE POSTING box. `checks` is a
 * bool[] of length 8; `onToggle(i)` flips one. Consumer is responsible
 * for gating the Submit button on `checks.every(Boolean)`.
 */
export function FinalCheckList({ checks, onToggle }) {
  const allChecked = useMemo(() => checks.every(Boolean), [checks]);
  return (
    <div
      data-testid="poster-final-checks"
      className="rounded-2xl border-2 border-amber-300 bg-white p-5 md:p-6"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <h3 className="font-display text-lg font-black uppercase tracking-wide text-amber-800">
          Final check before posting
        </h3>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Tick every point below to unlock the Post button — this is how we avoid mistakes on vendor ads.
      </p>
      <ul className="mt-4 space-y-2.5">
        {PRE_SUBMIT_CHECKS.map((label, i) => (
          <li key={i}>
            <label
              data-testid={`poster-check-${i + 1}`}
              className="flex items-start gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={!!checks[i]}
                onChange={() => onToggle(i)}
                className="mt-0.5 w-4 h-4 accent-amber-500"
                data-testid={`poster-check-input-${i + 1}`}
              />
              <span className={`text-sm leading-relaxed ${checks[i] ? "text-slate-500 line-through" : "text-slate-800"}`}>
                {label}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {!allChecked && (
        <div className="mt-4 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="poster-final-checks-hint">
          Tick every point above to unlock the Post button — this is how we avoid mistakes on vendor ads.
        </div>
      )}
    </div>
  );
}
